import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  reminderResource,
  type AttachmentRef,
  type AttachmentUploadInput,
  type CreateReminderInput,
  type NudgeReminderInput,
  type ReminderRef,
  type UpdateReminderInput,
} from './reminder.resource';

export const remindersQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['organizations', organizationId, 'reminders'],
    queryFn: () => reminderResource.list(organizationId),
  });

export function useReminders(organizationId: string | null) {
  return useQuery({
    ...remindersQuery(organizationId ?? ''),
    enabled: Boolean(organizationId),
    select: (data) => data.reminders,
  });
}

function useRefresh() {
  const client = useQueryClient();
  return (organizationId: string) =>
    client.invalidateQueries({
      queryKey: ['organizations', organizationId, 'reminders'],
    });
}

export function useCreateReminder() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: CreateReminderInput) => reminderResource.create(input),
    onSuccess: (_data, input) => refresh(input.organizationId),
  });
}

export function useUpdateReminder() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: UpdateReminderInput) => reminderResource.update(input),
    onSuccess: (_data, input) => refresh(input.organizationId),
  });
}

export function useNudgeReminder() {
  return useMutation({
    mutationFn: (input: NudgeReminderInput) => reminderResource.nudge(input),
  });
}

export function useDeleteReminder() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (ref: ReminderRef) => reminderResource.remove(ref),
    onSuccess: (_data, ref) => refresh(ref.organizationId),
  });
}

export function useAddAttachment() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: AttachmentUploadInput) => reminderResource.addAttachment(input),
    onSuccess: (_data, input) => refresh(input.organizationId),
  });
}

export function useRemoveAttachment() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (ref: AttachmentRef) => reminderResource.removeAttachment(ref),
    onSuccess: (_data, ref) => refresh(ref.organizationId),
  });
}

export const attachmentLoader =
  (ref: ReminderRef) =>
  (file: { id: string }): Promise<Blob> =>
    reminderResource.getAttachment({ ...ref, attachmentId: file.id });
